/**
 * Product Visibility presentation helpers — PURE, unit-testable functions for
 * the sellable badge, the "cache last checked" relative-time line, and the
 * summary counts. The server owns sellable state (Shopify inventory_policy);
 * this only drives how it renders.
 */
import type { InventoryPolicy, VisibilityRow } from '@/lib/schemas/visibility'

export interface SellableBadge {
  label: string
  /** Tailwind border/bg/text classes for the badge chip. */
  className: string
}

/** CONTINUE = "Sellable" (green), DENY = "Not sellable" (gray). */
export function sellableBadge(policy: InventoryPolicy): SellableBadge {
  return policy === 'CONTINUE'
    ? { label: 'Sellable', className: 'border-green-300 bg-green-50 text-green-800' }
    : { label: 'Not sellable', className: 'border-gray-300 bg-gray-50 text-gray-600' }
}

/** Max kh_checked_at across rows, or null when every row is null/empty. */
export function latestCheckedAt(rows: VisibilityRow[]): string | null {
  let latest: string | null = null
  let latestMs = -Infinity
  for (const r of rows) {
    if (r.kh_checked_at == null) continue
    const ms = new Date(r.kh_checked_at).getTime()
    if (Number.isNaN(ms)) continue
    if (ms > latestMs) {
      latestMs = ms
      latest = r.kh_checked_at
    }
  }
  return latest
}

/**
 * Relative time like "just now", "23 min ago", "3 hours ago", "2 days ago".
 * Returns "never" for null. `now` is injectable for testing.
 */
export function relativeTime(iso: string | null, now: Date = new Date()): string {
  if (iso == null) return 'never'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return 'never'
  const secs = Math.round((now.getTime() - then) / 1000)
  if (secs < 45) return 'just now'
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins} min ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
  const days = Math.round(hours / 24)
  return `${days} ${days === 1 ? 'day' : 'days'} ago`
}

export interface VisibilitySummary {
  total: number
  /** inventory_policy === 'CONTINUE' */
  sellable: number
  divergent: number
}

export function summarize(rows: VisibilityRow[]): VisibilitySummary {
  return {
    total: rows.length,
    sellable: rows.filter((r) => r.inventory_policy === 'CONTINUE').length,
    divergent: rows.filter((r) => r.divergent).length,
  }
}
